import React, { useState } from "react";
import { useProducts } from "../../../hooks/useProducts";
import { useCart } from "../../../contexts/CartContext";

const ProductsList: React.FC = () => {
  const { products, loading, error, totalItems } = useProducts(undefined, "20");
  const { addItem, getItemQuantity, incrementQuantity, decrementQuantity } = useCart();

  // Local state for quantity selection before adding to cart
  const [localQuantities, setLocalQuantities] = useState<Record<string, number>>({});

  const handleQuantityIncrease = (productId: string, product: any) => {
    const cartQty = getItemQuantity(productId);
    const maxInventory = product.inventory || 0;

    if (cartQty > 0) {
      // Item is in cart, check if we can increment
      if (cartQty < maxInventory) {
        incrementQuantity(productId);
      }
    } else {
      // Item not in cart, add it to cart with current local quantity
      const currentLocalQty = getLocalQuantity(productId);
      if (currentLocalQty <= maxInventory) {
        // Add to cart
        const discount = product.sale?.discount || 0;
        addItem(
          {
            id: product.id || "",
            name: product.name || "",
            sku: product.sku || "",
            price: product.price || 0,
            image: product.keyImage || "",
            discount: discount,
            shippingOptions: product.shippingOptions,
          },
          currentLocalQty
        );
        // Reset local quantity after adding to cart
        setLocalQuantities((prev) => {
          const updated = { ...prev };
          delete updated[productId];
          return updated;
        });
      }
    }
  };

  const handleQuantityDecrease = (productId: string) => {
    const cartQty = getItemQuantity(productId);

    if (cartQty > 0) {
      // Item is in cart, decrement cart quantity (removes if last one)
      decrementQuantity(productId);
    } else {
      // Item not in cart, decrement local quantity (minimum 1)
      setLocalQuantities((prev) => {
        const currentQty = prev[productId] || 1;
        const newQty = Math.max(1, currentQty - 1);
        return { ...prev, [productId]: newQty };
      });
    }
  };

  const isDecrementDisabled = (productId: string) => {
    const cartQty = getItemQuantity(productId);
    if (cartQty > 0) {
      // In cart: allow decrementing even from 1 (which removes the item)
      return false;
    }
    // Not in cart: can't go below local quantity of 1
    return getLocalQuantity(productId) <= 1;
  };

  const isIncrementDisabled = (productId: string, product: any) => {
    const cartQty = getItemQuantity(productId);
    const maxInventory = product.inventory || 0;

    // If item is already in cart, check cart quantity against inventory
    if (cartQty > 0) {
      return cartQty >= maxInventory;
    }

    // If item is not in cart, we can always add at least 1 item if inventory > 0
    // The button should only be disabled if inventory is 0
    return maxInventory === 0;
  };

  const isAddToCartDisabled = (productId: string, product: any) => {
    const cartQty = getItemQuantity(productId);
    const maxInventory = product.inventory || 0;

    // Disable if already at max inventory
    if (cartQty >= maxInventory) {
      return true;
    }

    // Disable if no inventory available
    return maxInventory === 0;
  };

  const getLocalQuantity = (productId: string) => localQuantities[productId] || 1;

  const handleAddToCart = (product: any) => {
    const productId = product.id || "";
    const cartQty = getItemQuantity(productId);
    const maxInventory = product.inventory || 0;

    // If already in cart and at max inventory, do nothing
    if (cartQty > 0 && cartQty >= maxInventory) {
      return;
    }

    // If already in cart, increment by 1
    if (cartQty > 0) {
      incrementQuantity(productId);
      return;
    }

    // Not in cart yet, add with local quantity
    const quantity = getLocalQuantity(productId);
    const discount = product.sale?.discount || 0;

    addItem(
      {
        id: productId,
        name: product.name || "",
        sku: product.sku || "",
        price: product.price || 0,
        image: product.keyImage || "",
        discount: discount,
        shippingOptions: product.shippingOptions,
      },
      quantity
    );

    // Reset local quantity after adding to cart
    setLocalQuantities((prev) => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  const getButtonText = (productId: string) => {
    const cartQty = getItemQuantity(productId);
    if (cartQty > 0) {
      return `${cartQty} Added`;
    }
    return `Add to Cart`;
  };

  if (loading) {
    return (
      <section className="products-list-section">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <p>Loading products...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="products-list-section">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <p className="text-danger">Error loading products: {error.message}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const isOutOfStock = (product: any) => {
    return product.status === "OUT_OF_STOCK" || product.inventory === 0;
  };

  const calculateSalePrice = (price: number, discountPercent: number) => {
    return price - (price * discountPercent / 100);
  };

  return (
    <section className="products-list-section" style={{ padding: "80px 0" }}>
      <div className="container">
        <div className="section-title text-center" style={{ marginBottom: "60px" }}>
          <h2 style={{ fontSize: "42px", fontWeight: "700", marginBottom: "10px" }}>Our Products</h2>
          <p style={{ color: "#666", fontSize: "16px" }}>Total Products: {totalItems}</p>
        </div>
        <div className="row" style={{ rowGap: "40px" }}>
          {products.map((product) => {
            const discount = product.sale?.discount || 0;
            const outOfStock = isOutOfStock(product);
            const originalPrice = product.price || 0;
            const displayPrice = discount > 0
              ? calculateSalePrice(originalPrice, discount)
              : originalPrice;

            return (
              <div key={product.id} className="col-lg-4 col-md-6 mb-4">
                <div
                  className="product-card"
                  onClick={() => window.location.href = `/products/${product.id}`}
                  style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-8px)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                >
                  <div className="product-image" style={{
                    position: "relative",
                    paddingTop: "100%",
                    backgroundColor: "white",
                    overflow: "hidden"
                  }}>
                    <img
                      src={product.keyImage}
                      alt={product.name}
                      style={{
                        position: "absolute",
                        top: "0",
                        left: "0",
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        objectPosition: "center"
                      }}
                    />
                    {discount > 0 && (
                      <span className="discount-badge" style={{
                        position: "absolute",
                        top: "15px",
                        left: "15px",
                        backgroundColor: "#dc3545",
                        color: "white",
                        padding: "5px 12px",
                        borderRadius: "4px",
                        fontSize: "14px",
                        fontWeight: "600",
                        zIndex: 2
                      }}>
                        -{discount}%
                      </span>
                    )}
                    {outOfStock && (
                      <span className="stock-badge" style={{
                        position: "absolute",
                        top: discount > 0 ? "55px" : "15px",
                        left: "15px",
                        backgroundColor: "#ff6b35",
                        color: "white",
                        padding: "5px 12px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        zIndex: 2
                      }}>
                        SOLD OUT
                      </span>
                    )}
                    {product.shippingOptions && product.shippingOptions.some((opt: any) => (opt.cost || 0) === 0) && (
                      <span className="free-shipping-badge" style={{
                        position: "absolute",
                        top: "15px",
                        right: "15px",
                        backgroundColor: "#28a745",
                        color: "white",
                        padding: "5px 12px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        zIndex: 2
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="1" y="3" width="15" height="13"></rect>
                          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                          <circle cx="5.5" cy="18.5" r="2.5"></circle>
                          <circle cx="18.5" cy="18.5" r="2.5"></circle>
                        </svg>
                        FREE SHIPPING
                      </span>
                    )}
                    {!outOfStock && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "20px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px",
                          zIndex: 2
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                          }}
                        >
                          <button
                            className="butn border rounded-circle"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleQuantityDecrease(product.id || "");
                            }}
                            disabled={isDecrementDisabled(product.id || "")}
                            title={isDecrementDisabled(product.id || "") ? "Minimum quantity is 1" : "Decrease quantity"}
                            style={{
                              width: "36px",
                              height: "36px",
                              fontSize: "20px",
                              fontWeight: "bold",
                              padding: "0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: isDecrementDisabled(product.id || "") ? 0.5 : 1,
                              cursor: isDecrementDisabled(product.id || "") ? "not-allowed" : "pointer"
                            }}
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <button
                            className="butn border rounded-pill hover-bg-black bg-orange1 text-white align-items-center"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAddToCart(product);
                            }}
                            disabled={isAddToCartDisabled(product.id || "", product)}
                            title={isAddToCartDisabled(product.id || "", product) ? `Maximum inventory reached (${product.inventory || 0} available)` : "Add to cart"}
                            style={{
                              whiteSpace: "nowrap",
                              minWidth: "140px",
                              opacity: isAddToCartDisabled(product.id || "", product) ? 0.5 : 1,
                              cursor: isAddToCartDisabled(product.id || "", product) ? "not-allowed" : "pointer"
                            }}
                          >
                            <span>{getButtonText(product.id || "")}</span>
                          </button>
                          <button
                            className="butn border rounded-circle"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleQuantityIncrease(product.id || "", product);
                            }}
                            disabled={isIncrementDisabled(product.id || "", product)}
                            title={isIncrementDisabled(product.id || "", product) ? `Maximum inventory reached (${product.inventory || 0} available)` : "Increase quantity"}
                            style={{
                              width: "36px",
                              height: "36px",
                              fontSize: "20px",
                              fontWeight: "bold",
                              padding: "0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: isIncrementDisabled(product.id || "", product) ? 0.5 : 1,
                              cursor: isIncrementDisabled(product.id || "", product) ? "not-allowed" : "pointer"
                            }}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                        {(() => {
                          const cartQty = getItemQuantity(product.id || "");
                          const maxInventory = product.inventory || 0;
                          const remaining = maxInventory - cartQty;

                          if (cartQty > 0 && remaining <= 3) {
                            return (
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: remaining === 0 ? "#dc3545" : "#ff9800",
                                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                                  padding: "3px 10px",
                                  borderRadius: "12px",
                                  fontWeight: "600",
                                  whiteSpace: "nowrap",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                                }}
                              >
                                {remaining === 0 ? "Inventory maxed out" : `Only ${remaining} left!`}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="product-content" style={{
                    padding: "25px 20px",
                    backgroundColor: "white",
                    flex: "1",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center"
                  }}>
                    <h3 style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      marginBottom: "15px",
                      color: "#333",
                      lineHeight: "1.4",
                      textAlign: "center"
                    }}>{product.name}</h3>
                    <div className="product-price" style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px"
                    }}>
                      {discount > 0 && (
                        <span style={{
                          textDecoration: "line-through",
                          color: "#999",
                          fontSize: "16px"
                        }}>
                          ${originalPrice.toFixed(2)}
                        </span>
                      )}
                      <span style={{
                        fontSize: "22px",
                        fontWeight: "700",
                        color: "#333"
                      }}>
                        ${displayPrice.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProductsList;
